import IWebPhoneService from '../IWebPhoneService';
import { callState } from '../state';
import { CallState } from '../types';
import handleCallEnded from './handleCallEnded';
import handleCallAnswered from './handleCallAnswered';
import handleCallPlaced from './handleCallPlaced';
import handleCallMissed from './handleCallMissed';
import IAppLogger from '@app/lib/IAppLogger';
import getPcsConversation, {
  PcsConversationType,
} from '../lib/getPcsConversation';

const handleWebPhoneCallStateUpdate = async (
  webPhoneService: IWebPhoneService,
  updatedCallState: CallState,
  logger: IAppLogger,
) => {
  let newConversation: PcsConversationType | undefined = undefined;
  const remoteCallIdChanged =
    updatedCallState.remoteCallId !== callState.remoteCallId;

  // if incoming remoteCallId is different from the current remoteCallId, fetch the conversationId
  if (updatedCallState.remoteCallId && remoteCallIdChanged) {
    try {
      newConversation = await getPcsConversation(updatedCallState.remoteCallId);
    } catch (error) {
      logger.error(error);
    }
  }

  // check for state changes
  const established = updatedCallState.established;

  if (!established && !callState.progress && updatedCallState.progress) {
    // pass pcsConversationId as part of the callState
    handleCallPlaced(
      webPhoneService,
      {
        ...updatedCallState,
        pcsConversationSid:
          newConversation?.sid ?? callState.pcsConversationSid,
        queueIds: newConversation?.queueIds ?? [],
      },
      logger,
    );
  } else if (!established && callState.progress && !updatedCallState.progress) {
    // by the time we get here, the call has already ended
    // and the callState has been reset. send the previous call state
    handleCallMissed(webPhoneService, callState, logger);
  } else if (callState.established && !updatedCallState.established) {
    // by the time we get here, the call has already ended
    // and the callState has been reset. send the previous call state
    handleCallEnded(webPhoneService, callState, logger);
  } else if (!callState.established && updatedCallState.established) {
    handleCallAnswered(webPhoneService, updatedCallState, logger);
  }

  // should be kept in sync with https://github.com/servicetitan/telecom-webphone/blob/master/packages/application/src/modules/SoftPhoneModule/state.ts
  callState.callId = updatedCallState.callId;
  callState.direction = updatedCallState.direction;
  callState.ended = updatedCallState.ended;
  callState.established = updatedCallState.established;
  callState.hasSession = updatedCallState.hasSession;
  callState.held = updatedCallState.held;
  callState.inTransfer = updatedCallState.inTransfer;
  callState.isAudioMuted = updatedCallState.isAudioMuted;
  callState.isVideoMuted = updatedCallState.isVideoMuted;
  callState.localIdentity = updatedCallState.localIdentity;
  callState.originating = updatedCallState.originating;
  callState.primary = updatedCallState.primary;
  callState.progress = updatedCallState.progress;
  callState.remoteIdentity = updatedCallState.remoteIdentity;
  callState.terminating = updatedCallState.terminating;
  callState.registrationFailed = updatedCallState.registrationFailed;
  callState.connected = updatedCallState.connected;
  callState.internalCall = updatedCallState.internalCall;
  callState.remoteCallId = updatedCallState.remoteCallId;
  callState.lastTriggeringEvent = updatedCallState.lastTriggeringEvent;

  // update callState fields with fields derived from the remoteCallId
  if (remoteCallIdChanged) {
    callState.pcsConversationSid = newConversation?.sid ?? '';
    callState.queueIds = newConversation?.queueIds ?? [];
  }
};

export default handleWebPhoneCallStateUpdate;
